import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from 'apisauce';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer, Subscription, timer } from 'rxjs';
import { debounceTime, delay, filter, mergeMap } from 'rxjs/operators';

import { HttpStatusCodeEnum, PredefinedMilisecondsTypeEnum } from '../../enums';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE_NUMBER, News, NewsGWResponse, RootNewsResponse } from '../../models/news';
import GlobalService from '../../services/Global.service';
import { NewsSubscriptionService } from '../../services/subscription/NewsSubscription.service';
import { SymbolNewsSubscriptionService } from '../../services/subscription/SymbolNewsSubscription.service';
import { isEmpty } from '../../util/typeGuards';
import { NullableString } from '../../util/types';
import { ErrorPayloadFull } from '../common-actions';
import { processErrorInResponse, processResponse } from '../helpers';
import { RootState } from '..';

import { GET_NEWS_CALL_LIMIT } from './constants';
import { getNewsRequestOrCacheKey, isNewsCacheTimeValid } from './helpers';
import {
  getLatestNews,
  getNewsByAccessId,
  getNewsByAccessIdCompleted,
  getNewsByAccessIdFailed,
  getNewsForSymbol,
  getSymbolNewsByAccessIdCompleted,
  getSymbolNewsByAccessIdFailed,
  setNewsLoading,
} from './index';

type NewsEpicState = RootState;

const getNewsEpic = (action$: Observable<any>, state$: StateObservable<NewsEpicState>) => action$.pipe(
  ofType(getLatestNews.type, getNewsForSymbol.type),
  debounceTime(PredefinedMilisecondsTypeEnum.halfSecond), // Debounce rapid successive requests
  filter(action => {
    const {
      latestNews,
      newsForSymbols,
      newsFetchedTimeByKey,
    } = state$.value.news;
    const symbol: NullableString = action.payload;
    const isSymbolSpecificRequest = !!symbol && (action.type === getNewsForSymbol.type);
    const latestNewsOrSymbolNews: News[] = isSymbolSpecificRequest ? newsForSymbols[symbol] : latestNews;
    const isNewsAvailable: boolean = !isEmpty(latestNewsOrSymbolNews);
    const isNewsAvailableAndCached = isNewsAvailable && isNewsCacheTimeValid(
      newsFetchedTimeByKey[getNewsRequestOrCacheKey(symbol)],
    );

    // prevent new request if it's already cached
    return !isNewsAvailableAndCached;
  }),
  mergeMap((action: PayloadAction<any>) => new Observable((observer: Observer<any>) => {
    const symbol: string | undefined = action.payload;
    const actionType = action.type;
    const isSymbolSpecificRequest = !!symbol && (actionType === getNewsForSymbol.type);

    observer.next(setNewsLoading(true));

    if (isSymbolSpecificRequest) {
      const symbolNewsSubscriptionService = SymbolNewsSubscriptionService.getInstance();
      symbolNewsSubscriptionService.subscribe(symbol, DEFAULT_PAGE_SIZE, FIRST_PAGE_NUMBER);
      return;
    }

    const newsSubscriptionService = NewsSubscriptionService.getInstance();
    newsSubscriptionService.subscribe();
  })),
);

const getNewsByAccessIdEpic = (action$: Observable<any>, state$: StateObservable<NewsEpicState>) => {
  const newsSubscriptionService = NewsSubscriptionService.getInstance();
  const symbolNewsSubscriptionService = SymbolNewsSubscriptionService.getInstance();
  const callCounts = new Map<string, number>();
  const retrySubscriptions = new Map<string, Subscription>();

  return action$.pipe(
    ofType(
      getNewsByAccessId.type,
    ),
    delay(PredefinedMilisecondsTypeEnum.oneSecond),
    mergeMap((action: PayloadAction<NewsGWResponse>) => new Observable((observer: Observer<any>) => {
      const { accessId, symbol, traceIdentifier } = action.payload ?? {};
      const isSymbolSpecificRequest = !!symbol;
      const callName = 'news/getNewsByAccessId';

      const currentCount = callCounts.get(accessId) ?? 0;
      if (currentCount >= GET_NEWS_CALL_LIMIT) {
        retrySubscriptions.get(accessId)?.unsubscribe();
        // Dispatch proper completion action to finalize state after retry limit reached
        const errorStr = 'News request timeout after maximum retries';
        const errorPayload = {
          callName,
          errorStr,
          errorData: null,
          requestParams: action.payload,
        } as ErrorPayloadFull;
        observer.next(isSymbolSpecificRequest
          ? getSymbolNewsByAccessIdFailed({ customData: symbol, ...errorPayload })
          : getNewsByAccessIdFailed(errorPayload));
        callCounts.delete(accessId);
        retrySubscriptions.delete(accessId);

        return;
      }

      GlobalService.gatewayRest.get(accessId)
        .then((response: ApiResponse<RootNewsResponse[]>) => {
          if (response.status === HttpStatusCodeEnum.NotFound) {
            return { ...response, data: [] };
          }
          return response;
        })
        .then((response: ApiResponse<RootNewsResponse[]>) => {
          if (isEmpty(response.data)) {
            const nextCount = (callCounts.get(accessId) ?? 0) + 1;
            callCounts.set(accessId, nextCount);
            retrySubscriptions.get(accessId)?.unsubscribe();
            const sub = timer(PredefinedMilisecondsTypeEnum.threeSeconds)
              .subscribe(() => {
                observer.next(getNewsByAccessId(action.payload)); // Re-dispatch the action
                retrySubscriptions.delete(accessId);
              });
            retrySubscriptions.set(accessId, sub);
            return;
          }

          // cleanup any retry state for this accessId
          retrySubscriptions.get(accessId)?.unsubscribe();
          retrySubscriptions.delete(accessId);
          callCounts.delete(accessId);

          processResponse(
            callName,
            {},
            response,
            observer,
            isSymbolSpecificRequest ? getSymbolNewsByAccessIdCompleted : getNewsByAccessIdCompleted,
            isSymbolSpecificRequest ? getSymbolNewsByAccessIdFailed : getNewsByAccessIdFailed,
            false,
            isSymbolSpecificRequest ? { symbol } : undefined,
          );
        })
        .catch(error => {
          retrySubscriptions.get(accessId)?.unsubscribe();
          retrySubscriptions.delete(accessId);
          callCounts.delete(accessId);

          processErrorInResponse(callName, error, action, observer, getNewsByAccessIdFailed);
        })
        .finally(() => {
          if (isSymbolSpecificRequest) {
            symbolNewsSubscriptionService.unsubscribe(symbol);
            return;
          }

          newsSubscriptionService.unsubscribe();
        });
    })),
  );
};

export default combineEpics(
  getNewsByAccessIdEpic,
  getNewsEpic,
);
